import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocationNextApi } from '@fmnts/api/next';
import { createHttpUploadRequestEffectHandler } from '@fmnts/common/store';
import {
  TextToastData,
  ToastActionId,
  ToastComponent,
  Toaster,
} from '@fmnts/components/toast';
import { fromHttpOrGeneralError } from '@formunauts/shared/errors/domain';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, filter, map, of, tap } from 'rxjs';
import { NextLocationsOfflineStoreService } from '../next-locations-offline-store.service';
import {
  NextLocationCheckInOfflineActions,
  NextLocationCheckInOfflineUploadActions,
} from './next-locations.actions';

const toastSuccessData: TextToastData = {
  title: 'Location was uploaded successfully',
  type: 'success',
};

const toastErrorData: TextToastData = {
  title: 'Upload failed',
  message: 'Uploading Location failed.',
  actions: [
    {
      actionId: ToastActionId('viewDetails'),
      content: 'View Details',
    },
  ],
  type: 'error',
};
@Injectable()
export class NextOfflineStoreLocationsEffects {
  private locationOfflineStore = inject(NextLocationsOfflineStoreService);
  private actions$ = inject(Actions);
  private locationNextApi = inject(LocationNextApi);
  private toaster = inject(Toaster);
  private router = inject(Router);

  hydrate$ = createEffect(() => {
    return of(null).pipe(
      map(() => this.locationOfflineStore.hydrate()),
      map((locations) =>
        NextLocationCheckInOfflineActions.hydrate({
          locations,
        }),
      ),
    );
  });

  persist$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NextLocationCheckInOfflineActions.persist),
        map(({ checkIn }) => this.locationOfflineStore.persistCheckIn(checkIn)),
      );
    },
    { dispatch: false },
  );

  removeOffline$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NextLocationCheckInOfflineUploadActions.success),
        tap((response) =>
          this.locationOfflineStore.removePersistedCheckIn(
            response.response.dataKey,
          ),
        ),
      );
    },
    { dispatch: false },
  );

  uploadLocationFromStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationCheckInOfflineUploadActions.request),
      concatMap(
        createHttpUploadRequestEffectHandler(
          NextLocationCheckInOfflineUploadActions,
          {
            run: ({ payload: formData, dataKey, inBackground }) =>
              this.locationNextApi.checkIn(formData).pipe(
                map((result) => ({
                  response: result,
                  dataKey,
                  payload: formData,
                  inBackground,
                })),
              ),
            onError: (error, { dataKey, inBackground }) =>
              fromHttpOrGeneralError('fmnts')(error).pipe(
                map((result) => ({
                  error: result.error,
                  dataKey,
                  inBackground,
                })),
              ),
          },
        ),
      ),
    );
  });

  uploadLocationSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NextLocationCheckInOfflineUploadActions.success),
        tap(() => {
          const data = toastSuccessData;
          return this.toaster.openFromComponent(ToastComponent, {
            data,
            duration: 20000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
          });
        }),
      );
    },
    { dispatch: false },
  );

  uploadLocationFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NextLocationCheckInOfflineUploadActions.failure),
        filter((error) => error.inBackground),
        tap(() => {
          const data = toastErrorData;
          return this.toaster
            .openFromComponent(ToastComponent, {
              data,
              duration: 20000,
              horizontalPosition: 'right',
              verticalPosition: 'bottom',
            })
            .afterDismissed()
            .subscribe((actionId: any) => {
              if (actionId.result === 'viewDetails') {
                // Navigate to the offline-store page when 'View Details' action is clicked
                this.router.navigate(['/next/stored']);
              }
            });
        }),
      );
    },
    { dispatch: false },
  );
}
